var __defProp = Object.defineProperty;
var __defProps = Object.defineProperties;
var __getOwnPropDescs = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps = (a, b) => __defProps(a, __getOwnPropDescs(b));
import { jsx } from "react/jsx-runtime";
import { memo, useEffect } from "react";
import { useSelector } from "react-redux";
import { ConfigProvider, Grid, theme } from "antd";
const themeConfig = {
  colorWhite: "#ffffff",
  colorBlack: "#000000",
  colorBgBase: "#0F0F0F",
  colorBgContainer: "#1E1E1E",
  borderRadius: "16px",
  borderRadiusSM: "12px",
  borderRadiusXS: "8px",
  borderRadiusXXS: "4px",
  colorTextBase: "#F7F7F7",
  colorTextWhite: "#F7F7F7",
  colorLink: "#F7F7F7",
  colorLinkActive: "#8470FF",
  colorLinkHover: "#F7F7F7",
  colorLinkSecondary: "#C9C9C9",
  colorBgSolid: "#2A2A2A",
  colorBgCard: "#141414",
  colorTableBgSolid: "#2A2A2A",
  colorLinkBoxHoverBg: "#2A2A2A",
  colorBgActive: "#5D43FF",
  activeBorderColor: "#8470FF",
  hoverBorderColor: "#8470FF",
  colorTextPlaceholder: "#838383",
  colorTextHeading: "#F7F7F7",
  colorTextSecondary: "#C9C9C9",
  colorBorder: "#555555",
  colorTableBorder: "#555555",
  colorNatural: "#E0E0E0",
  colorLiveRed: "#FF1940",
  colorBlackWhite: "#fff",
  colorOverlayBG: "rgba(0, 0, 0, 0.4)",
  colorHighlight: "#FFC700",
  boxShadow: "0px 1px 4px 0px rgba(0, 0, 0, 0.05), 0px 16px 24px 0px rgba(0, 0, 0, 0.05)",
  fontFamily: "Inter, sans-serif",
  secondaryFontFamily: "Oswald, sans-serif",
  fontSize: 14,
  fontSizeLG: 16,
  fontSizeXS: 12,
  fontSizeSM: 13,
  fontSizeXL: 18,
  fontSizeXXL: 20,
  fontSizeHeading2: 28,
  fontSizeHeading3: 24,
  lineHeightLG: "24px"
};
const lightThemeConfig = __spreadProps(__spreadValues({}, themeConfig), {
  colorBgBase: "#F5F5F5",
  colorBgContainer: "#ffffff",
  colorTextBase: "#222222",
  colorTextHeading: "#222222",
  colorTextSecondary: "#555555",
  colorBorder: "#C9C9C9",
  colorTableBorder: "#E0E0E0",
  colorNatural: "#555555",
  colorBgSolid: "#F5F5F5",
  colorBgCard: "#ffffff",
  colorTableBgSolid: "#EBEBEB",
  colorLinkBoxHoverBg: "#fbfbfb",
  colorBgActive: "#5D43FF",
  activeBorderColor: "#5D43FF",
  hoverBorderColor: "#5D43FF",
  colorTextPlaceholder: "#838383",
  colorLink: "#222222",
  colorLinkActive: "#5D43FF",
  colorLinkSecondary: "#555555",
  colorLinkHover: "#222222",
  colorBlackWhite: "#000",
  colorOverlayBG: "rgba(255, 255, 255, 0.4)",
  colorHighlight: "#B38600"
  // Add more overrides as needed
});
const { useBreakpoint } = Grid;
const ThemeProvider = ({ children }) => {
  const storeTheme = useSelector((state) => {
    var _a;
    return (_a = state.theme) == null ? void 0 : _a.mode;
  });
  const screens = useBreakpoint();
  const mode = storeTheme || "dark";
  const config = mode === "dark" ? themeConfig : lightThemeConfig;
  useEffect(() => {
    if (typeof document === "undefined")
      return;
    const root = document.documentElement;
    Object.entries(config).forEach(([key, value]) => {
      const cssVariableName = `--${key.replace(/[A-Z]/g, (match) => `-${match.toLowerCase()}`)}`;
      root.style.setProperty(cssVariableName, value);
    });
  }, [mode]);
  return /* @__PURE__ */ jsx(
    ConfigProvider,
    {
      theme: {
        algorithm: mode === "dark" ? theme.darkAlgorithm : theme.defaultAlgorithm,
        token: __spreadValues({}, config),
        components: {
          Layout: { bodyBg: config.colorBgBase },
          Input: {
            borderRadius: 8,
            colorBorder: config.colorBorder,
            colorBgContainer: config.colorBgBase,
            paddingBlock: "8px",
            colorText: config.colorTextBase,
            fontSize: "14px",
            controlOutline: "none"
          },
          Button: {
            fontWeight: 700,
            fontSizeLG: 16,
            paddingBlockLG: 12,
            paddingInlineLG: 18,
            borderRadiusLG: 8,
            borderRadius: 8,
            colorText: config.colorTextBase,
            defaultBorderColor: "transparent",
            defaultHoverBorderColor: "transparent",
            defaultActiveBorderColor: config.colorBorder,
            defaultHoverBg: config.colorBgBase,
            defaultHoverColor: config.colorTextBase,
            defaultActiveBg: config.colorBgBase,
            colorBgContainer: mode === "dark" ? "#2A2A2A" : "#E0E0E0",
            colorPrimary: "#FBFAFF",
            colorPrimaryHover: config.colorTextBase,
            colorPrimaryActive: config.colorTextBase,
            colorTextLightSolid: config.colorWhite
          },
          Menu: {
            itemColor: config.colorTextBase,
            itemHoverColor: config.colorTextBase,
            fontSize: 16,
            lineHeight: "24px"
          },
          Dropdown: {
            borderRadiusLG: 8,
            fontSize: 16,
            colorText: config.colorTextBase
          },
          Switch: {
            colorPrimary: config.colorLinkActive
          },
          Table: {
            cellPaddingInline: 5,
            cellPaddingBlock: screens.md ? 16 : 12,
            cellFontSize: screens.md ? config.fontSize : config.fontSizeXS
          },
          Rate: {
            starColor: "#FF6B00"
          }
        }
      },
      children
    }
  );
};
var stdin_default = memo(ThemeProvider);
export {
  stdin_default as default
};
